import cn from "classnames"
import { useState } from "react"
import { Outlet, useSearchParams } from "react-router-dom"

import { Button, Layout } from "@/atoms"
import { noScopeSelected } from "@/domain/user"
import { preventDefault } from "@/helpers/utils"
import { useLogger } from "@/hooks/useLogger"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { LeftOutlined, RightOutlined } from "@/icons"
import { Drawers } from "@/organisms/Drawers"
import { DRAWERS_PARAM_NAME } from "@/organisms/Drawers/const"
import { ErrorBoundary } from "@/organisms/ErrorBoundary"
import { KaiChat, useKai } from "@/organisms/KaiChat"
import { NoScopeResult } from "@/organisms/NoScopeResult"
import { SideNavigation } from "@/organisms/SideNavigation"
import { TaskProvider } from "@/organisms/TaskProvider"
import { ReportProvider } from "@/pages/ReportsPage/components/organisms/ReportProvider"
import { InfoBanner } from "./InfoBanner"

import styles from "./LayoutTemplate.module.scss"

export const LayoutTemplate = () => {
  const { logger } = useLogger()
  const [_collapsed, setCollapsed] = useState(false)
  const { Sider } = Layout
  const { user } = useUser()
  const showScopePlaceHolder = noScopeSelected(user)
  const trialDays = useFeatureFlags("trialDays")
  const chatbotEnabled = useFeatureFlags("chatbotEnabled")
  const showInfoBanner = !!trialDays && trialDays > 0
  const { open, toggle } = useKai()
  const [searchParams] = useSearchParams()
  const isDrawersOpen = !!searchParams.get(DRAWERS_PARAM_NAME)
  const collapsed = _collapsed || open || isDrawersOpen

  return (
    <Layout
      className={cn(styles.containerLayoutTemplate, open && styles.chatOpened)}
    >
      <Sider width={220} collapsed={collapsed} onCollapse={setCollapsed}>
        <Button
          size="small"
          disabled={open}
          className={styles.collapsedButton}
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setCollapsed(!collapsed)}
        />
        <SideNavigation collapsed={collapsed} user={user} />
        {isDrawersOpen && (
          <div
            className="mask-drawer-ks"
            onClick={(e) => {
              preventDefault(e)

              const masks = document.getElementsByClassName(
                "ant-drawer-mask"
              ) as HTMLCollectionOf<HTMLDivElement>

              masks?.[masks.length - 1]?.click()
            }}
          />
        )}
      </Sider>
      <Layout
        id="outlet-layout-container"
        className={cn(
          styles.outletSection,
          collapsed && styles.collapsed,
          showInfoBanner && styles.showInfoBanner,
          open && styles.outletSectionChatOpened
        )}
      >
        <ErrorBoundary user={user} logger={logger}>
          <TaskProvider>
            {showInfoBanner && <InfoBanner days={trialDays} />}
            {showScopePlaceHolder && <NoScopeResult />}
            {!showScopePlaceHolder && (
              <ReportProvider>
                <Outlet />
              </ReportProvider>
            )}
            <Drawers />
          </TaskProvider>
        </ErrorBoundary>
      </Layout>
      {chatbotEnabled && (
        <KaiChat open={open} toggle={toggle} user={user} logger={logger} />
      )}
    </Layout>
  )
}
